import type { ContractorApplicantFile } from '../types/contractorOnboard';

/** Sentinel leadId for contractor onboard packet viewers. */
export const CONTRACTOR_ONBOARD_VIEWER_LEAD_ID = '__contractor_onboard__';

export type ContractorOnboardPacketKind = 'w9' | 'id';

const PACKET_DOCUMENT_RE = /^contractor-onboard:([^:]+):(w9|id)$/;

export function buildContractorOnboardDocumentId(
  applicantId: string,
  kind: ContractorOnboardPacketKind,
): string {
  return `contractor-onboard:${applicantId}:${kind}`;
}

export function parseContractorOnboardDocumentId(documentId?: string | null): {
  applicantId: string;
  kind: ContractorOnboardPacketKind;
} | null {
  const match = PACKET_DOCUMENT_RE.exec((documentId || '').trim());
  if (!match) return null;
  return {
    applicantId: match[1],
    kind: match[2] as ContractorOnboardPacketKind,
  };
}

export function isContractorOnboardLeadId(leadId?: string | null): boolean {
  return (leadId || '').trim() === CONTRACTOR_ONBOARD_VIEWER_LEAD_ID;
}

export function isContractorOnboardPdf(
  file?: Pick<ContractorApplicantFile, 'fileName' | 'contentType'> | null,
): boolean {
  const name = (file?.fileName || '').toLowerCase();
  const contentType = (file?.contentType || '').toLowerCase().split(';')[0].trim();
  return contentType === 'application/pdf' || name.endsWith('.pdf');
}

export function isContractorOnboardImage(
  file?: Pick<ContractorApplicantFile, 'fileName' | 'contentType'> | null,
): boolean {
  const name = (file?.fileName || '').toLowerCase();
  const contentType = (file?.contentType || '').toLowerCase().split(';')[0].trim();
  if (contentType.startsWith('image/')) return true;
  return /\.(jpe?g|png|webp|gif)$/i.test(name);
}

export type ContractorOnboardViewerKind = 'pdf' | 'image';

export function contractorOnboardViewerKind(
  file?: Pick<ContractorApplicantFile, 'fileName' | 'contentType'> | null,
): ContractorOnboardViewerKind | null {
  if (isContractorOnboardPdf(file)) return 'pdf';
  if (isContractorOnboardImage(file)) return 'image';
  return null;
}

export type ContractorOnboardViewerOpen = {
  url: string;
  title: string;
  documentId: string;
  kind: ContractorOnboardViewerKind;
  mimeType?: string;
  leadName?: string;
};
